import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, retry } from 'rxjs';
import { Injectable } from '@angular/core';
import { NotificationBarService } from '../thirdParty/ngx-notification-bar/notification-bar.service';
import { NotificationType } from '../thirdParty/ngx-notification-bar/index';

@Injectable()
export class ErrorIntercept implements HttpInterceptor {

  // Flag to track if an error notification is currently visible
  // Used to prevent showing multiple notifications in rapid succession
  private isErrorNotificationVisible = false;

  // Cooldown time in milliseconds between showing error notifications
  // For example, 5000 ms = 5 seconds
  private readonly NOTIFICATION_COOLDOWN = 5000;

  constructor(private notifications: NotificationBarService){}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> | Observable<any> {
    return next.handle(request)
      .pipe(
        retry(1), // Retry the failed request once
        catchError((error: HttpErrorResponse) => {
          console.error('HTTP Error:', error); // Use console.error for errors

          let errorMessage = '';

          // --- Logic for formatting the error message ---
          // Check the error status
          if (error.status === 404) {
            errorMessage = 'Resource not found (404).';
          } else if (error.status >= 500) {
            errorMessage = 'Server error. Please try again later.';
          } else if (error.status === 401) {
            errorMessage = 'Unauthorized access.';
          } else if (error.status === 403) {
            errorMessage = 'Access denied.';
          } else if (error.status === 400) {
            errorMessage = 'Bad request.';
          }


          // Check for messages in the response body
          // Use the ?. operator for safe access to nested properties
          if (error.error?.message) {
            errorMessage += (errorMessage ? ' ' : '') + error.error.message; // Add space if errorMessage is not empty
          }
          if (error.error?.errorDescription) {
            errorMessage += (errorMessage ? ' ' : '') + error.error.errorDescription;
          }
          if (error.error?.error_description) {
            errorMessage += (errorMessage ? ' ' : '') + error.error.error_description;
          }
          if (error.error?.ErrorInfo) {
            errorMessage += (errorMessage ? ' ' : '') + error.error.ErrorInfo;
          }

          // Logic for specific errors, e.g., password change
          const isPasswordChange = request.body?.confirmPassword && request.body?.newPassword;
          if (error.error?.errorMessage && error.error.errorMessage.indexOf('_') === -1 && request.body) {
            errorMessage += (errorMessage ? ' ' : '') + (isPasswordChange ? 'Error.' : error.error.errorMessage);
          }
          if (error.error?.errorDescription && request.body) {
            if (isPasswordChange) {
              errorMessage += (errorMessage ? '<br/>' : '') + 'This password has been used previously. Please create a new password.';
            } else {
              errorMessage += (errorMessage ? '<br/>' : '') + error.error.errorDescription;
            }
          }

          // Handling validation errors
          if (error.error?.validationErrors) {
            errorMessage = ''; // Clear previous message if there are validation errors
            Object.values(error.error.validationErrors).forEach((messages: any) => {
              if (Array.isArray(messages)) {
                messages.forEach(msg => {
                  errorMessage += msg + '<br/>'; // Add line break for each error
                });
              } else {
                errorMessage += messages + '<br/>';
              }
            });
            errorMessage = errorMessage.trim(); // Remove trailing spaces/line breaks
          }

          // Handling ModelState errors (often used in .NET Web API)
          if (error.error?.modelState) {
            errorMessage = ''; // Clear previous message
            let counter = 0;
            for (const key in error.error.modelState) {
              if (error.error.modelState.hasOwnProperty(key)) {
                const messages = error.error.modelState[key];
                if (Array.isArray(messages)) {
                  messages.forEach(message => {
                    if (counter > 0) {
                      errorMessage += '<br/>';
                    }
                    errorMessage += message;
                    counter++;
                  });
                }
              }
            }
            if (errorMessage) { // Add code tags only if there is a message
              errorMessage = `<code>${errorMessage}</code>`;
            }
          }
          // --- End of logic for formatting the error message ---


          // --- Logic for showing the notification with a cooldown ---
          // Show the notification only if it's not a 'Token' request
          // And if another error notification is not currently visible (thanks to the flag)
          // And if the formatted error message is not empty
          if (!request.url.includes('Token') && !this.isErrorNotificationVisible && errorMessage) {
            this.isErrorNotificationVisible = true; // Set the flag that a notification is visible

            // Create the error notification
            this.notifications.create({ message: errorMessage, type: NotificationType.Error, });

            // Start a timer that will reset the flag after NOTIFICATION_COOLDOWN ms
            // This allows showing new notifications after the cooldown period ends
            setTimeout(() => {
              this.isErrorNotificationVisible = false;
            }, this.NOTIFICATION_COOLDOWN);
          }
          // --- End of cooldown logic ---


          // Logic for saving the error code in localStorage (as in your original code)
          if(typeof error.error === 'string'){
            localStorage.setItem('code', error.error);
          }else{
            localStorage.removeItem('code');
          }

          // Important: always re-throw the error so it's available to the request subscribers
          // Use throwError(() => value) to pass the value in RxJS 7+
          return throwError(() => errorMessage);
        })
      );
  }
}
